import axios from "axios";
import { useEffect, useState } from "react";

/**
 * "Waking the server up" notice (B-60).
 *
 * The backend runs on a free Render instance that sleeps when idle. The first
 * requests after it falls asleep take up to a minute, and every screen used to
 * sit on a bare spinner with "(0)" counts and no word of why.
 *
 * The signal is SILENCE, not slowness: the notice shows once something has been
 * waiting on the server for WAKE_NOTICE_AFTER_MS and no reply of any kind (a
 * success or an HTTP error) has come back in that time. A slow photo upload
 * while the badge polls keep answering is a busy server, not a sleeping one, so
 * it shows nothing. A request that fails without a reply (offline) only stops
 * counting as waiting.
 *
 * Nor can a server that answered in the last AWAKE_FOR_MS be asleep: Render's
 * free plan only sleeps after 15 minutes with no requests. A long wait then is
 * a slow endpoint (an admin email to 20 people), so it shows nothing either.
 *
 * Counts the shared axios instance, which every call to our API goes through.
 * Registered once, at import; the store is module state so the interceptors and
 * the component need no provider between them.
 */
export const WAKE_NOTICE_AFTER_MS = 5000;
// Under Render's 15 minutes, so a real sleep is never mistaken for awake.
export const AWAKE_FOR_MS = 600000;

let pending = 0;
// When the server last answered, or when the oldest current wait began.
let quietSince = 0;
let lastReply = -AWAKE_FOR_MS;
const listeners = new Set();
const changed = () => listeners.forEach((fn) => fn());

// Any reply proves the server is awake; only watched requests count as waiting.
const settled = (config, replied) => {
  if (!config || config.wakeWatch) pending = Math.max(0, pending - 1);
  if (replied) quietSince = lastReply = Date.now();
  changed();
};

// `?.` only for test files that replace axios with a bare { get, post } mock
// and still import the shell; the real axios always has interceptors.
axios.interceptors?.request.use((config) => {
  // A photo upload is slow on a phone network whatever the server is doing,
  // so it never counts as waiting (its reply still counts as a reply).
  if (config.data instanceof FormData) return config;
  config.wakeWatch = true;
  if (pending++ === 0) quietSince = Date.now();
  changed();
  return config;
});
axios.interceptors?.response.use(
  (response) => {
    settled(response.config, true);
    return response;
  },
  (error) => {
    settled(error?.config, !!error?.response);
    return Promise.reject(error);
  }
);

const ServerWakeNotice = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    let timer;
    const check = () => {
      clearTimeout(timer);
      const now = Date.now();
      const silent =
        pending > 0 && now - lastReply >= AWAKE_FOR_MS ? now - quietSince : -1;
      setShow(silent >= WAKE_NOTICE_AFTER_MS);
      if (silent >= 0 && silent < WAKE_NOTICE_AFTER_MS) {
        timer = setTimeout(check, WAKE_NOTICE_AFTER_MS - silent);
      }
    };
    listeners.add(check);
    check();
    return () => {
      listeners.delete(check);
      clearTimeout(timer);
    };
  }, []);

  // Styled by .tl-wake-notice in index.css (no cost to the main JS chunk).
  return show ? (
    <div role="status" className="tl-wake-notice">
      Waking the server up. This can take up to a minute on first open.
    </div>
  ) : null;
};

export default ServerWakeNotice;
